// src/pages/consultations/ConsultationsList.jsx
import { useState, useEffect, useCallback } from "react";
import {
  Search,
  Loader2,
  AlertCircle,
  CheckCircle,
  X,
  Eye,
  Mail,
  User,
  MessageCircle,
  Calendar,
  ArrowLeft,
  Circle,
  CheckCircle2,
} from "lucide-react";
import api from "../../api/axios";

/* ─── Styles (نفس نمط باقي الصفحات) ─────────────────────────────────────── */
const STYLES = `
  .cons-page { --accent:#4f7cff; --accent-2:#8b5cf6; --accent-glow:rgba(79,124,255,0.22); }

  .cons-card {
    background: var(--glass-bg);
    backdrop-filter: blur(20px) saturate(160%);
    -webkit-backdrop-filter: blur(20px) saturate(160%);
    border: 1px solid var(--glass-border);
    border-radius: 18px;
    transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.4s ease;
    cursor: pointer;
  }
  .cons-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgba(79,124,255,0.12);
  }

  .cons-stat {
    background: var(--glass-bg);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border: 1px solid var(--glass-border);
    border-radius: 16px;
    padding: 16px;
    transition: background 0.4s ease;
  }

  .cons-input {
    background: var(--glass-bg);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border: 1px solid var(--glass-border);
    border-radius: 12px;
    color: var(--text-primary);
    transition: all 0.2s ease, background 0.4s ease;
    outline: none;
  }
  .cons-input:focus {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px var(--accent-glow);
    background: var(--glass-active);
  }
  .cons-input::placeholder { color: var(--text-secondary); }

  .cons-filter-btn {
    background: var(--glass-bg);
    border: 1px solid var(--glass-border);
    border-radius: 10px; padding: 8px 16px;
    color: var(--text-secondary); font-size: 12.5px; font-weight: 600;
    cursor: pointer; transition: all 0.2s ease; white-space: nowrap;
  }
  .cons-filter-btn.active {
    background: linear-gradient(135deg, var(--accent), var(--accent-2));
    border-color: transparent; color: #fff;
    box-shadow: 0 4px 16px var(--accent-glow);
  }
  .cons-filter-btn:not(.active):hover { background: var(--glass-hover); color: var(--text-primary); }

  .cons-btn-ghost {
    background: var(--glass-bg);
    border: 1px solid var(--glass-border);
    border-radius: 12px; color: var(--text-primary);
    font-size: 13px; font-weight: 600; cursor: pointer;
    transition: all 0.2s ease;
  }
  .cons-btn-ghost:hover { background: var(--glass-hover); }

  .cons-modal-overlay {
    position: fixed; inset: 0; z-index: 50;
    background: rgba(0,0,0,0.45);
    backdrop-filter: blur(8px);
    display: flex; align-items: center; justify-content: center; padding: 16px;
  }
  .cons-modal {
    background: var(--dropdown-bg);
    backdrop-filter: blur(28px) saturate(180%);
    -webkit-backdrop-filter: blur(28px) saturate(180%);
    border: 1px solid var(--glass-border);
    border-radius: 24px;
    box-shadow: 0 24px 80px rgba(0,0,0,0.2);
    width: 100%; max-width: 560px;
    max-height: 90vh; overflow-y: auto;
    animation: consModalIn 0.25s cubic-bezier(0.34,1.56,0.64,1);
  }
  @keyframes consModalIn {
    from { opacity:0; transform:scale(0.93) translateY(12px); }
    to   { opacity:1; transform:scale(1) translateY(0); }
  }

  .cons-badge {
    font-size: 10.5px; font-weight: 700; padding: 4px 11px; border-radius: 20px;
    display: inline-flex; align-items: center; gap: 5px;
  }
  .cons-badge.received { background: rgba(251,191,36,0.14); color: #d97706; border: 1px solid rgba(251,191,36,0.25); }
  .cons-badge.forwarded_to_teacher { background: rgba(79,124,255,0.14); color: var(--accent); border: 1px solid rgba(79,124,255,0.25); }
  .cons-badge.forwarded_to_student { background: rgba(52,211,153,0.15); color: #10b981; border: 1px solid rgba(52,211,153,0.25); }

  .cons-avatar {
    width: 42px; height: 42px; border-radius: 12px;
    background: linear-gradient(135deg, rgba(79,124,255,0.15), rgba(139,92,246,0.15));
    border: 1.5px solid rgba(79,124,255,0.2);
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }

  .gl-empty {
    text-align: center; padding: 80px 24px;
    background: var(--glass-bg);
    backdrop-filter: blur(12px);
    border: 1.5px dashed var(--glass-border);
    border-radius: 20px;
  }
  .gl-empty-icon { color: var(--text-secondary); opacity: 0.3; margin: 0 auto 16px; }

  .cons-page-title { font-size: 22px; font-weight: 800; color: var(--text-primary); }
  .cons-page-subtitle { font-size: 12.5px; color: var(--text-secondary); margin-top: 2px; }

  .stat-icon-wrap {
    width: 34px; height: 34px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }

  /* Stepper */
  .cons-stepper { display: flex; flex-direction: column; gap: 0; }
  .cons-step {
    display: flex; align-items: flex-start; gap: 14px;
    padding: 4px 0;
  }
  .cons-step-line {
    width: 2px; flex: 1; min-height: 28px;
    background: var(--glass-border);
    margin: 2px 0 2px 17px;
  }
  .cons-step-line.done { background: linear-gradient(180deg, var(--accent), #10b981); }
  .cons-step-icon-wrap {
    width: 36px; height: 36px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0; border: 2px solid var(--glass-border);
    background: var(--glass-bg);
    transition: all 0.25s ease;
  }
  .cons-step-icon-wrap.done {
    background: linear-gradient(135deg, var(--accent), var(--accent-2));
    border-color: transparent;
    box-shadow: 0 4px 14px var(--accent-glow);
  }
  .cons-step-icon-wrap.current {
    border-color: var(--accent);
    box-shadow: 0 0 0 4px var(--accent-glow);
  }
  .cons-step-content { flex: 1; padding-top: 6px; }
  .cons-step-title { font-size: 13.5px; font-weight: 700; color: var(--text-primary); }
  .cons-step-title.pending { color: var(--text-secondary); font-weight: 500; }
  .cons-step-action {
    margin-top: 8px;
  }

  .gl-scroll::-webkit-scrollbar { width: 4px; }
  .gl-scroll::-webkit-scrollbar-track { background: transparent; }
  .gl-scroll::-webkit-scrollbar-thumb { background: var(--scroll-thumb); border-radius: 99px; }
`;

function injectStyles() {
  const id = "cons-styles";
  if (!document.getElementById(id)) {
    const tag = document.createElement("style");
    tag.id = id;
    tag.textContent = STYLES;
    document.head.appendChild(tag);
  }
}

/* ─── Status metadata ───────────────────────────────────────────────────── */
const STATUS_STEPS = [
  { key: "received", label: "تم استلام الاستشارة" },
  { key: "forwarded_to_teacher", label: "تم تحويل الاستشارة للمدرس" },
  { key: "forwarded_to_student", label: "تم تحويل الاستشارة للطالب" },
];

const STATUS_LABELS = {
  received: "تم الاستلام",
  forwarded_to_teacher: "محوّلة للمدرس",
  forwarded_to_student: "محوّلة للطالب",
};

function statusIndex(status) {
  return STATUS_STEPS.findIndex((s) => s.key === status);
}

/* ─── Toast ──────────────────────────────────────────────────────────────── */
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div
      style={{
        position: "fixed",
        top: 20,
        right: 20,
        zIndex: 999,
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "12px 18px",
        borderRadius: 16,
        fontSize: 13.5,
        fontWeight: 600,
        color: "#fff",
        boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
        background:
          type === "success"
            ? "linear-gradient(135deg,#10b981,#059669)"
            : "linear-gradient(135deg,#ef4444,#dc2626)",
      }}
    >
      {type === "success" ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
      {message}
      <button
        onClick={onClose}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "rgba(255,255,255,0.7)",
          display: "flex",
        }}
      >
        <X size={15} />
      </button>
    </div>
  );
}

/* ─── Status Badge ───────────────────────────────────────────────────────── */
function StatusBadge({ status }) {
  return (
    <span className={`cons-badge ${status}`}>
      <Circle size={7} fill="currentColor" />
      {STATUS_LABELS[status] || status}
    </span>
  );
}

/* ─── Detail Modal ───────────────────────────────────────────────────────── */
function ConsultationDetailModal({ consultationId, onClose, onUpdated, onToast }) {
  const [consultation, setConsultation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/booking/consultations/${consultationId}/`);
      setConsultation(res.data);
    } catch {
      onToast("فشل في تحميل تفاصيل الاستشارة", "error");
      onClose();
    } finally {
      setLoading(false);
    }
  }, [consultationId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const advanceStatus = async (nextStatus) => {
    setUpdating(true);
    try {
      const res = await api.patch(
        `/booking/consultations/${consultationId}/status/`,
        { status: nextStatus }
      );
      setConsultation(res.data.consultation || res.data);
      onToast("تم تحديث حالة الاستشارة بنجاح", "success");
      onUpdated();
    } catch {
      onToast("فشل في تحديث الحالة", "error");
    } finally {
      setUpdating(false);
    }
  };

  const currentIdx = consultation ? statusIndex(consultation.status) : -1;

  return (
    <div
      className="cons-modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="cons-modal">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "22px 28px 18px",
            borderBottom: "1px solid var(--glass-border)",
          }}
        >
          <div>
            <h2 style={{ fontSize: 17, fontWeight: 800, color: "var(--text-primary)" }}>
              تفاصيل الاستشارة
            </h2>
            <p style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 3 }}>
              #{consultationId}
            </p>
          </div>
          <button
            onClick={onClose}
            className="cons-btn-ghost"
            style={{
              width: 34,
              height: 34,
              padding: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <X size={15} color="var(--text-secondary)" />
          </button>
        </div>

        {loading || !consultation ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "60px 0",
            }}
          >
            <Loader2 size={32} style={{ animation: "spin 1s linear infinite", color: "var(--accent)" }} />
          </div>
        ) : (
          <div
            className="gl-scroll"
            style={{
              padding: "22px 28px 28px",
              display: "flex",
              flexDirection: "column",
              gap: 20,
              overflowY: "auto",
            }}
            dir="rtl"
          >
            {/* العنوان والحالة الحالية */}
            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                <h3 style={{ fontSize: 16, fontWeight: 800, color: "var(--text-primary)", lineHeight: 1.4 }}>
                  {consultation.title}
                </h3>
                <StatusBadge status={consultation.status} />
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  marginTop: 8,
                  color: "var(--text-secondary)",
                  fontSize: 11.5,
                }}
              >
                <Calendar size={12} />
                {new Date(consultation.created_at).toLocaleString("ar-EG")}
              </div>
            </div>

            {/* الوصف */}
            <div
              style={{
                background: "var(--glass-bg)",
                border: "1px solid var(--glass-border)",
                borderRadius: 14,
                padding: 16,
              }}
            >
              <p style={{ fontSize: 11.5, fontWeight: 700, color: "var(--text-secondary)", marginBottom: 8 }}>
                الوصف التفصيلي
              </p>
              <p style={{ fontSize: 13, color: "var(--text-primary)", lineHeight: 1.8, whiteSpace: "pre-line" }}>
                {consultation.description}
              </p>
            </div>

            {/* الطالب والمدرس */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  background: "var(--glass-bg)",
                  border: "1px solid var(--glass-border)",
                  borderRadius: 14,
                  padding: 12,
                }}
              >
                <div className="cons-avatar">
                  <User size={17} color="var(--accent)" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 10.5, color: "var(--text-secondary)" }}>الطالب</p>
                  <p style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>
                    {consultation.student_name}
                  </p>
                  {consultation.student_email && (
                    <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 2 }}>
                      <Mail size={10} color="var(--text-secondary)" />
                      <span style={{ fontSize: 10.5, color: "var(--text-secondary)", direction: "ltr" }}>
                        {consultation.student_email}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  background: "var(--glass-bg)",
                  border: "1px solid var(--glass-border)",
                  borderRadius: 14,
                  padding: 12,
                }}
              >
                <div className="cons-avatar">
                  <User size={17} color="#8b5cf6" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 10.5, color: "var(--text-secondary)" }}>المدرس</p>
                  <p style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>
                    {consultation.teacher_name}
                  </p>
                  {consultation.teacher_email && (
                    <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 2 }}>
                      <Mail size={10} color="var(--text-secondary)" />
                      <span style={{ fontSize: 10.5, color: "var(--text-secondary)", direction: "ltr" }}>
                        {consultation.teacher_email}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Stepper */}
            <div>
              <p style={{ fontSize: 11.5, fontWeight: 700, color: "var(--text-secondary)", marginBottom: 12 }}>
                مراحل الاستشارة
              </p>
              <div className="cons-stepper">
                {STATUS_STEPS.map((step, idx) => {
                  const done = idx < currentIdx || idx === currentIdx;
                  const isCurrent = idx === currentIdx;
                  const isNext = idx === currentIdx + 1;
                  const isLast = idx === STATUS_STEPS.length - 1;
                  return (
                    <div key={step.key}>
                      <div className="cons-step">
                        <div
                          className={`cons-step-icon-wrap ${done ? "done" : ""} ${
                            isCurrent ? "current" : ""
                          }`}
                        >
                          {done ? (
                            <CheckCircle2 size={17} color="#fff" />
                          ) : (
                            <Circle size={14} color="var(--text-secondary)" />
                          )}
                        </div>
                        <div className="cons-step-content">
                          <p className={`cons-step-title ${done ? "" : "pending"}`}>
                            {step.label}
                          </p>
                          {isCurrent && (
                            <p style={{ fontSize: 11, color: "var(--accent)", marginTop: 2, fontWeight: 600 }}>
                              المرحلة الحالية
                            </p>
                          )}
                          {isNext && (
                            <div className="cons-step-action">
                              <button
                                onClick={() => advanceStatus(step.key)}
                                disabled={updating}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 6,
                                  padding: "7px 14px",
                                  background:
                                    "linear-gradient(135deg, var(--accent), var(--accent-2))",
                                  border: "none",
                                  borderRadius: 10,
                                  color: "#fff",
                                  fontSize: 12,
                                  fontWeight: 700,
                                  cursor: "pointer",
                                  opacity: updating ? 0.6 : 1,
                                }}
                              >
                                {updating ? (
                                  <Loader2 size={12} style={{ animation: "spin 1s linear infinite" }} />
                                ) : (
                                  <ArrowLeft size={12} />
                                )}
                                نقل لهذه المرحلة
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                      {!isLast && (
                        <div className={`cons-step-line ${idx < currentIdx ? "done" : ""}`} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Consultation Card ──────────────────────────────────────────────────── */
function ConsultationCard({ consultation, onOpen }) {
  return (
    <div className="cons-card" onClick={() => onOpen(consultation.id)}>
      <div style={{ padding: 18 }} dir="rtl">
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
          <h3
            style={{
              fontSize: 14,
              fontWeight: 800,
              color: "var(--text-primary)",
              lineHeight: 1.4,
              flex: 1,
            }}
          >
            {consultation.title}
          </h3>
          <StatusBadge status={consultation.status} />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--text-secondary)" }}>
            <User size={12} />
            <span style={{ fontSize: 12 }}>
              طالب: <strong style={{ color: "var(--text-primary)" }}>{consultation.student_name}</strong>
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--text-secondary)" }}>
            <User size={12} />
            <span style={{ fontSize: 12 }}>
              مدرس: <strong style={{ color: "var(--text-primary)" }}>{consultation.teacher_name}</strong>
            </span>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginTop: 14,
            paddingTop: 12,
            borderTop: "1px solid var(--glass-border)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 5, color: "var(--text-secondary)" }}>
            <Calendar size={11} />
            <span style={{ fontSize: 11 }}>
              {new Date(consultation.created_at).toLocaleDateString("ar-EG")}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 5, color: "var(--accent)", fontWeight: 700, fontSize: 11.5 }}>
            <Eye size={12} />
            عرض التفاصيل
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Stat Card ──────────────────────────────────────────────────────────── */
function StatCard({ label, value, icon: Icon, iconBg, iconColor, valueColor }) {
  return (
    <div className="cons-stat">
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <div className="stat-icon-wrap" style={{ background: iconBg }}>
          <Icon size={15} color={iconColor} />
        </div>
        <span style={{ fontSize: 11.5, color: "var(--text-secondary)", fontWeight: 500 }}>
          {label}
        </span>
      </div>
      <p style={{ fontSize: 24, fontWeight: 900, color: valueColor || "var(--text-primary)" }}>
        {value}
      </p>
    </div>
  );
}

/* ─── Main Page ──────────────────────────────────────────────────────────── */
export default function ConsultationsList() {
  useEffect(() => {
    injectStyles();
  }, []);

  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [openId, setOpenId] = useState(null);
  const [toast, setToast] = useState(null);

  const fetchConsultations = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus !== "all") params.status = filterStatus;
      const res = await api.get("/booking/consultations/all/", { params });
      setConsultations(res.data.consultations || []);
    } catch {
      showToast("فشل في تحميل الاستشارات", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConsultations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus]);

  const showToast = (message, type = "success") => setToast({ message, type });

  const filtered = consultations.filter((c) => {
    const q = search.toLowerCase();
    return (
      c.title.toLowerCase().includes(q) ||
      c.student_name?.toLowerCase().includes(q) ||
      c.teacher_name?.toLowerCase().includes(q)
    );
  });

  const stats = {
    total: consultations.length,
    received: consultations.filter((c) => c.status === "received").length,
    forwardedTeacher: consultations.filter((c) => c.status === "forwarded_to_teacher").length,
    forwardedStudent: consultations.filter((c) => c.status === "forwarded_to_student").length,
  };

  return (
    <div className="cons-page" style={{ display: "flex", flexDirection: "column", gap: 24 }} dir="rtl">
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      {openId && (
        <ConsultationDetailModal
          consultationId={openId}
          onClose={() => setOpenId(null)}
          onUpdated={fetchConsultations}
          onToast={showToast}
        />
      )}

      {/* Header */}
      <div>
        <h1 className="cons-page-title">إدارة الاستشارات</h1>
        <p className="cons-page-subtitle">{stats.total} طلب استشارة</p>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14 }}>
        <StatCard
          label="إجمالي الطلبات"
          value={stats.total}
          icon={MessageCircle}
          iconBg="rgba(79,124,255,0.12)"
          iconColor="var(--accent)"
          valueColor="var(--accent)"
        />
        <StatCard
          label="تم الاستلام"
          value={stats.received}
          icon={Circle}
          iconBg="rgba(251,191,36,0.12)"
          iconColor="#f59e0b"
          valueColor="#f59e0b"
        />
        <StatCard
          label="محوّلة للمدرس"
          value={stats.forwardedTeacher}
          icon={ArrowLeft}
          iconBg="rgba(79,124,255,0.12)"
          iconColor="var(--accent)"
          valueColor="var(--accent)"
        />
        <StatCard
          label="محوّلة للطالب"
          value={stats.forwardedStudent}
          icon={CheckCircle2}
          iconBg="rgba(52,211,153,0.12)"
          iconColor="#10b981"
          valueColor="#10b981"
        />
      </div>

      {/* Filters */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
        <div style={{ position: "relative", flex: 1, minWidth: 220 }}>
          <Search
            size={15}
            color="var(--text-secondary)"
            style={{
              position: "absolute",
              right: 14,
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
            }}
          />
          <input
            type="text"
            placeholder="ابحث بالعنوان أو اسم الطالب أو المدرس..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="cons-input"
            style={{ width: "100%", padding: "10px 44px 10px 16px", fontSize: 13.5 }}
          />
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {[
            ["all", "الكل"],
            ["received", "تم الاستلام"],
            ["forwarded_to_teacher", "محوّلة للمدرس"],
            ["forwarded_to_student", "محوّلة للطالب"],
          ].map(([val, label]) => (
            <button
              key={val}
              onClick={() => setFilterStatus(val)}
              className={`cons-filter-btn ${filterStatus === val ? "active" : ""}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "80px 0",
            gap: 12,
          }}
        >
          <Loader2 size={36} style={{ animation: "spin 1s linear infinite", color: "var(--accent)" }} />
          <p style={{ color: "var(--text-secondary)", fontSize: 13 }}>جاري التحميل...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="gl-empty">
          <MessageCircle size={52} className="gl-empty-icon" />
          <h3 style={{ fontSize: 15, fontWeight: 700, color: "var(--text-primary)", marginBottom: 6 }}>
            {search ? "لا توجد نتائج" : "لا توجد طلبات استشارة بعد"}
          </h3>
          <p style={{ fontSize: 13, color: "var(--text-secondary)" }}>
            {search ? "جرب البحث بكلمة أخرى" : "هتظهر هنا أي طلبات استشارة جديدة من الطلاب"}
          </p>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))",
            gap: 16,
          }}
        >
          {filtered.map((c) => (
            <ConsultationCard key={c.id} consultation={c} onOpen={setOpenId} />
          ))}
        </div>
      )}

      <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}